import { ConfigProvider } from 'antd'
import { Locale } from 'antd/lib/locale'
import enUsLocale from 'antd/lib/locale/en_US'
import idIDLocale from 'antd/lib/locale/id_ID'
import _ from 'lodash'
import 'moment/locale/id'
import { Suspense, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { IntlProvider } from 'react-intl'
import { Outlet, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { formatPath } from '../utils/routes'
import { ConfigGuard } from '../components/ConfigGuard'
import { PageLayout } from '../components/layout'
import { PageLoader } from '../components/Spin'
import { DialogRenderer } from '../containers/DialogRenderer'
import { useAppConfig } from '../hooks/useAppConfig'
import { useScrollToTop } from '../hooks/useScrollToTop'
import { importLazy } from '../utils/import'
import { Dictionary } from '../utils/types'
import { LocationActions } from '../redux/location'
import {
  getInvalidContractConfirmed,
  getInvalidContractDetected,
  getSessionTimeoutConfirmed,
  getSessionTimeoutDetected
} from '../redux/user/selectors'
import { UserActions } from '../redux/user'
import { DialogActions } from '../redux/dialog'
import { getCmsHomeUrl } from '../redux/config'

const localeMap: Dictionary<Locale> = {
  en: enUsLocale,
  id: idIDLocale
}

const UnlockApiPage = importLazy(() => import('../pages/UnlockApi'))

export const MainLayout = () => {
  const { primaryLanguage, availableLanguages, images } = useAppConfig()
  const { i18n } = useTranslation(['routes'])
  const reactRouterlocation = useLocation()
  const dispatch = useDispatch()
  useScrollToTop()

  const { pathname } = reactRouterlocation
  let locale = pathname.substring(1, 3).toLowerCase()

  if (!_.includes(availableLanguages, locale)) {
    locale = primaryLanguage
  }

  useEffect(() => {
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale)
    }
  }, [locale, i18n])

  useEffect(() => {
    dispatch(LocationActions.setLocation({ currentLocationPath: pathname }))
  }, [dispatch, pathname])

  const invalidContractDetected = useSelector(getInvalidContractDetected)
  const invalidContractConfirmed = useSelector(getInvalidContractConfirmed)
  const sessionTimeoutDetected = useSelector(getSessionTimeoutDetected)
  const sessionTimeoutConfirmed = useSelector(getSessionTimeoutConfirmed)
  const home = useSelector(getCmsHomeUrl)

  useEffect(() => {
    if (!home || _.size(pathname) > 4) return
    location.replace(formatPath(home))
  }, [home, pathname])

  useEffect(() => {
    if (invalidContractDetected) {
      if (invalidContractConfirmed) {
        dispatch(UserActions.logout({ isLoginAsMode: false }))
      } else {
        setTimeout(() => {
          dispatch(
            DialogActions.showInfoDialog({
              i18nText: 'dialogs:ContractInformation.ContractError',
              i18nTitle: 'core:Warning',
              namespaces: ['dialogs']
            })
          )
        }, 2000)
      }
    }
  }, [dispatch, invalidContractDetected, invalidContractConfirmed])

  useEffect(() => {
    if (sessionTimeoutDetected) {
      if (sessionTimeoutConfirmed) {
        dispatch(UserActions.logout({ isLoginAsMode: false }))
      } else {
        dispatch(
          DialogActions.showInfoDialog({
            i18nText: 'dialogs:SessionTimeout.Text',
            i18nTitle: 'dialogs:SessionTimeout.Title',
            namespaces: ['dialogs']
          })
        )
      }
    }
  }, [dispatch, sessionTimeoutDetected, sessionTimeoutConfirmed])

  return (
    <IntlProvider locale={locale || 'en'} defaultLocale="en">
      <ConfigProvider locale={localeMap[locale!] || localeMap.en}>
        <DialogRenderer />
        <ConfigGuard language={locale || 'en'} alertImg={images['alertCircleBlue']}>
          <PageLayout
            render={apiBlocked => (
              <Suspense fallback={<PageLoader />}>{apiBlocked ? <UnlockApiPage /> : <Outlet />}</Suspense>
            )}
          />
        </ConfigGuard>
      </ConfigProvider>
    </IntlProvider>
  )
}
